import React, { useCallback, useMemo } from 'react'
import { Form, Select as AntSelect, Typography } from 'antd'
import produce from 'immer'
import { difference, includes, map, noop, size } from 'lodash'
import PropTypes from 'prop-types'

import { ALL_SELECT_OPTION_ITEM, DROPDOWN_MIN_WIDTH } from '../../constants'
import { antFormItemNamePropType } from '../../prop-types'
import {
  filterSelectOptions,
  formatFormLabel,
  getFormItemLabelAboveConfiguration,
} from '../../utils'

const Select = ({
  value,
  onChange,
  name,
  options,
  mode,
  disabled,
  ...rest
}) => {
  const handleOnChange = useCallback(
    selectedValues => {
      if (includes(selectedValues, ALL_SELECT_OPTION_ITEM.value)) {
        const newItemsSelected = difference(selectedValues, value)

        if (
          size(newItemsSelected) === 1 &&
          value.length > 0 &&
          includes(newItemsSelected, ALL_SELECT_OPTION_ITEM.value)
        ) {
          onChange([])
        } else {
          const [, ...optionsWithoutAll] = options
          const allValues = map(optionsWithoutAll, 'value')
          onChange(allValues)
        }
      } else {
        onChange(selectedValues)
      }
    },
    [onChange, options, value],
  )

  const maxTagPlaceholder = useCallback(
    omittedValues => `and ${size(omittedValues)} more selected`,
    [],
  )

  return (
    <AntSelect
      onChange={handleOnChange}
      value={value}
      mode={mode}
      maxTagCount={2}
      maxTagPlaceholder={maxTagPlaceholder}
      filterOption={filterSelectOptions}
      dropdownStyle={{ minWidth: 'fit-content' }}
      options={options}
      dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
      disabled={disabled}
      {...rest}
    />
  )
}

Select.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  name: antFormItemNamePropType.isRequired,
  options: PropTypes.array.isRequired,
  mode: PropTypes.string,
  disabled: PropTypes.bool,
}

Select.defaultProps = {
  mode: undefined,
  value: undefined,
  disabled: false,
  onChange: noop,
}

const FormSelect = ({
  name,
  label,
  options,
  mode,
  rules,
  labelAbove,
  showAllOption,
  showColon,
  disabled,
  ...rest
}) => {
  const optionsWithAll = useMemo(
    () =>
      showAllOption && includes(['multiple', 'tags'], mode)
        ? produce(options, newOptions => {
            newOptions.unshift(ALL_SELECT_OPTION_ITEM)
            return newOptions
          })
        : options,
    [showAllOption, mode, options],
  )

  return (
    <Form.Item
      labelCol={getFormItemLabelAboveConfiguration(labelAbove)}
      name={name}
      label={
        <Typography.Text>{formatFormLabel(label, showColon)}</Typography.Text>
      }
      rules={rules}
    >
      <Select
        name={name}
        label={label}
        options={optionsWithAll}
        mode={mode}
        disabled={disabled}
        {...rest}
      />
    </Form.Item>
  )
}

FormSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  mode: PropTypes.oneOf(['multiple', 'tags']),
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  labelAbove: PropTypes.bool,
  showAllOption: PropTypes.bool,
  showColon: PropTypes.bool,
  disabled: PropTypes.bool,
}

FormSelect.defaultProps = {
  label: '',
  mode: undefined,
  rules: undefined,
  labelAbove: false,
  showAllOption: false,
  showColon: true,
  disabled: false,
}

export default FormSelect
